/**
 * Zeit- und Kalenderrechnung, fest auf Europe/Berlin.
 *
 * Zwei getrennte Welten, die nie vermischt werden dürfen:
 *  - **Zeitstempel** (`number`, ms seit Epoch, UTC) — echte Zeitpunkte.
 *  - **Tagesschlüssel** (`string`, "YYYY-MM-DD") — Kalendertage in Berlin.
 *
 * Kalenderarithmetik läuft ausschliesslich über `Date.UTC` auf den
 * Tagesschlüsseln. Das ist absichtlich: rechnet man Tage in lokaler Zeit,
 * liefern die beiden DST-Wechsel im Jahr 23- bzw. 25-Stunden-Tage und damit
 * falsche Wochengrenzen.
 */

export const TZ = 'Europe/Berlin';
export const DAY_MS = 86_400_000;
export const HOUR_MS = 3_600_000;
export const MINUTE_MS = 60_000;

export type DayKey = string; // "YYYY-MM-DD"

const pad = (n: number): string => String(n).padStart(2, '0');

// en-CA formatiert als "YYYY-MM-DD" — genau unser Tagesschlüssel.
const dayKeyFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const clockFmt = new Intl.DateTimeFormat('de-DE', {
  timeZone: TZ,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const partsFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/** Kalendertag in Berlin, zu dem dieser Zeitpunkt gehört. */
export function dayKey(ts: number): DayKey {
  return dayKeyFmt.format(ts);
}

/** Uhrzeit "HH:MM" in Berlin. */
export function hhmm(ts: number): string {
  return clockFmt.format(ts);
}

/** Offset von Berlin gegenüber UTC zum Zeitpunkt `ts`, in ms. */
function berlinOffset(ts: number): number {
  const p: Record<string, string> = {};
  for (const part of partsFmt.formatToParts(ts)) {
    if (part.type !== 'literal') p[part.type] = part.value;
  }
  // hour kann bei hour12:false als "24" kommen (Mitternacht).
  const asIfUtc = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour) % 24,
    Number(p.minute),
    Number(p.second),
  );
  return asIfUtc - Math.floor(ts / 1000) * 1000;
}

/**
 * Berliner Wanduhrzeit -> echter Zeitpunkt.
 *
 * Zwei Durchläufe, weil der Offset selbst vom Ergebnis abhängt: der erste
 * Versuch kann auf der falschen Seite eines DST-Wechsels landen. Existiert die
 * Uhrzeit nicht (Sprung vorwärts, 02:30 im März), ergibt sich der nächste
 * gültige Zeitpunkt — das ist für Korrekturen die richtige Auflösung.
 */
export function berlinToTs(day: DayKey, time: string): number {
  const [y, m, d] = day.split('-').map(Number);
  const [hh, mi] = time.split(':').map(Number);
  const naive = Date.UTC(y, m - 1, d, hh || 0, mi || 0);
  const first = naive - berlinOffset(naive);
  return naive - berlinOffset(first);
}

// --- Kalenderarithmetik auf Tagesschlüsseln (DST-frei, da rein UTC) ---

function civil(day: DayKey): number {
  const [y, m, d] = day.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

function civilKey(ms: number): DayKey {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export function addDays(day: DayKey, n: number): DayKey {
  return civilKey(civil(day) + n * DAY_MS);
}

/** Wochentag, 0 = Sonntag ... 6 = Samstag. */
export function dayOfWeek(day: DayKey): number {
  return new Date(civil(day)).getUTCDay();
}

/** Montag der Woche, in der `day` liegt. */
export function weekStart(day: DayKey): DayKey {
  return addDays(day, -((dayOfWeek(day) + 6) % 7));
}

/** Die sieben Tagesschlüssel ab Montag. */
export function weekKeys(monday: DayKey): DayKey[] {
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

/** Alle Tagesschlüssel des Monats, in dem `day` liegt. */
export function monthKeys(day: DayKey): DayKey[] {
  const [y, m] = day.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: last }, (_, i) => `${y}-${pad(m)}-${pad(i + 1)}`);
}

/**
 * Kalenderwoche nach ISO 8601 (DIN 1355) — die Woche, in der der Donnerstag
 * liegt, gehört zum Jahr dieses Donnerstags. Deshalb der Umweg über den
 * Donnerstag statt einer simplen Division seit Jahresbeginn.
 */
export function isoWeek(day: DayKey): number {
  const thursday = new Date(civil(day));
  thursday.setUTCDate(thursday.getUTCDate() - ((thursday.getUTCDay() + 6) % 7) + 3);
  const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
  firstThursday.setUTCDate(firstThursday.getUTCDate() - ((firstThursday.getUTCDay() + 6) % 7) + 3);
  return 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / (7 * DAY_MS));
}

export const isWeekend = (day: DayKey): boolean =>
  dayOfWeek(day) === 0 || dayOfWeek(day) === 6;

export const isSunday = (day: DayKey): boolean => dayOfWeek(day) === 0;

// --- Formatierung ---

const DOW_DE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const MONTH_DE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

export const dowLabel = (day: DayKey): string => DOW_DE[dayOfWeek(day)];
export const monthLabel = (day: DayKey): string => MONTH_DE[Number(day.split('-')[1]) - 1];

/** "21.09." — kompakt für Tabellenzeilen. */
export function dateLabel(day: DayKey): string {
  const [, m, d] = day.split('-');
  return `${d}.${m}.`;
}

/** "21.09.2026" */
export function fullDateLabel(day: DayKey): string {
  const [y, m, d] = day.split('-');
  return `${d}.${m}.${y}`;
}

/** "Montag, 21. September 2026" — Kopfzeile Terminal. */
export function longDateLabel(day: DayKey): string {
  const long = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  const [y, m, d] = day.split('-');
  return `${long[dayOfWeek(day)]}, ${Number(d)}. ${MONTH_DE[Number(m) - 1]} ${y}`;
}

/** Dauer als "8:05 h". Negative Werte mit echtem Minuszeichen. */
export function fmtDuration(ms: number): string {
  const neg = ms < 0;
  const rounded = Math.abs(Math.round(ms / MINUTE_MS) * MINUTE_MS);
  const h = Math.floor(rounded / HOUR_MS);
  const m = Math.round((rounded % HOUR_MS) / MINUTE_MS);
  return `${neg ? '−' : ''}${h}:${pad(m)} h`;
}

/** Saldo als "+8:05 h" / "−2:15 h". */
export function fmtSigned(ms: number): string {
  return (ms >= 0 ? '+' : '') + fmtDuration(ms);
}

/** Laufende Uhr, "HH:MM:SS" oder "HH:MM". Nie negativ. */
export function fmtClock(ms: number, withSeconds = true): string {
  const v = Math.max(0, ms);
  const h = Math.floor(v / HOUR_MS);
  const m = Math.floor((v % HOUR_MS) / MINUTE_MS);
  const s = Math.floor((v % MINUTE_MS) / 1000);
  return withSeconds ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}`;
}

/** Dezimalstunden für den CSV-Export, deutsches Komma. */
export function fmtDecimalHours(ms: number): string {
  return (ms / HOUR_MS).toFixed(2).replace('.', ',');
}
